import React, { useEffect, useState } from "react";
import { Table, Input, DatePicker, Select, Col, Row } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllCallHistories } from "./redux/actions";
import CustomPagination from "../../components/customPagination/CustomPagination";
import ReceiversTable from "./ReceiversTable";
import { capitalizeFirstLetterofEachWord } from "../../globalUtils/capitalizeFirstLetterofEachWord";
import { userTypeOptions } from "../UserManager/utils";
import moment from "moment";
import _ from "lodash";

const { Option } = Select;

const CallingTable = () => {
  const [sortedInfo, setSortedInfo] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [page, setPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [req, setReq] = useState(false);
  const [search, setSearch] = useState({});
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const dispatch = useDispatch();

  const { allCallHistories, loading, totalRecords } = useSelector(
    (state) => state.callHistoriesReducer
  );

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 800)
  );
  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 800)
  );

  useEffect(() => {
    if (localStorage.getItem("callingManagerState")) {
      const localStorageData = JSON.parse(
        localStorage.getItem("callingManagerState")
      );
      if (localStorageData) {
        setTimeout(() => {
          setSearch(localStorageData.search || {});
          setPage(localStorageData.page || 1);
          setResultPerPage(localStorageData.resultPerPage || 10);
          if (localStorageData?.sortedInfo) {
            setSortedInfo(localStorageData.sortedInfo);
          }
          debouncedCallApi();
        }, 500);
      }
    }
  }, []);

  useEffect(() => {
    if (page && resultPerPage && page > 0 && resultPerPage > 0) {
      dispatch(
        fetchAllCallHistories({
          page,
          limit: resultPerPage,
          search,
          sort: sortedInfo?.order && sortedInfo?.columnKey,
          order: sortedInfo?.order
            ? sortedInfo?.order === "ascend"
              ? 1
              : -1
            : null,
        })
      );
    }
  }, [req, page, resultPerPage, sortedInfo, search, dispatch]);

  useEffect(() => {
    if (defaultPageOneReq) {
      setPage(1);
    }
  }, [defaultPageOneReq]);

  useEffect(() => {
    filterData();
  }, [allCallHistories, search]);

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  const paginatedData = dataSource.slice(
    (page - 1) * resultPerPage,
    page * resultPerPage
  );

  const handleSearch = (key, value, func = null) => {
    const updatedState = { ...search, [key]: value };
    setSearch(updatedState);

    if (typeof func === "function") func();

    localStorage.setItem(
      "callingManagerState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("callingManagerState") || "{}"),
        search: updatedState,
        page: 1,
      })
    );
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) =>
        handleSearch(key, e.target.value, debouncedDefaultPageOneCallApi)
      }
      placeholder=""
    />
  );

  const dateInput = (key) => (
    <DatePicker
      placeholder=""
      style={{ width: "100%" }}
      suffixIcon={null}
      format="DD MMM YYYY"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => handleSearch(key, e, debouncedDefaultPageOneCallApi)}
      onOpenChange={(open) => setDatePickerOpened(open)}
    />
  );

  const handleNavigate = (id) => {
    window.location.href = `/users/${id}`;
  };

  const filterData = () => {
    let filtered = allCallHistories;

    if (search["sender.user_id"]) {
      filtered = filtered.filter((entry) =>
        entry?.sender?.user_id
          ?.toLowerCase()
          .includes(search["sender.user_id"].toLowerCase())
      );
    }

    if (search["sender.name"]) {
      filtered = filtered.filter((entry) =>
        entry?.sender?.name
          ?.toLowerCase()
          .includes(search["sender.name"].toLowerCase())
      );
    }

    if (search["callInitiatedAt"]) {
      filtered = filtered.filter(
        (entry) =>
          entry.callInitiatedAt &&
          moment.utc(entry.callInitiatedAt).local().format("DD MMM YYYY") ===
            moment(search["callInitiatedAt"]).format("DD MMM YYYY")
      );
    }

    if (search["callEndedAt"]) {
      filtered = filtered.filter(
        (entry) =>
          entry.callEndedAt &&
          moment.utc(entry.callEndedAt).local().format("DD MMM YYYY") ===
            moment(search["callEndedAt"]).format("DD MMM YYYY")
      );
    }

    if (search["initiatorRole"]) {
      filtered = filtered.filter((entry) => {
        const role = entry.initiatorRole;
        if (!role) return false;
        if (typeof role === "string") return role === search["initiatorRole"];
        if (Array.isArray(role)) return role.includes(search["initiatorRole"]);
        return false;
      });
    }

    if (search["callDuration"]) {
      filtered = filtered.filter((entry) => {
        const d = entry.callDuration || 0;
        const formatted = `${Math.floor(d / 60)}m ${Math.floor(d % 60)}s`;
        return formatted
          .toLowerCase()
          .includes(search["callDuration"].toLowerCase());
      });
    }

    if (search["type"]) {
      filtered = filtered.filter((entry) =>
        (entry.type || "").toLowerCase().includes(search["type"].toLowerCase())
      );
    }

    setDataSource(filtered);
  };

  const columns = [
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">User ID</div>
          {searchInput("sender.user_id")}
        </div>
      ),
      dataIndex: ["sender", "user_id"],
      key: "user_id",
      sorter: true,
      width: "20",
      sortOrder: sortedInfo?.columnKey === "user_id" && sortedInfo?.order,
      render: (_, record) => record?.sender?.user_id || "",
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Name</div>
          {searchInput("sender.name")}
        </div>
      ),
      dataIndex: ["sender", "name"],
      key: "sender",
      ellipsis: true,
      sorter: true,
      width: "15%",
      sortOrder: sortedInfo?.columnKey === "sender" && sortedInfo?.order,
      render: (_, record) => record?.sender?.name || "",
    },

    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Start Time</div>
          {dateInput("callInitiatedAt")}
        </div>
      ),
      dataIndex: "callInitiatedAt",
      key: "callInitiatedAt",
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === "callInitiatedAt" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (val) =>
        val ? moment.utc(val).local().format("DD MMM YYYY hh:mm A") : "",
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">End Time</div>
          {dateInput("callEndedAt")}
        </div>
      ),
      dataIndex: "callEndedAt",
      key: "callEndedAt",
      sorter: true,
      width: "15%",
      sortOrder: sortedInfo?.columnKey === "callEndedAt" && sortedInfo?.order,
      ellipsis: true,
      render: (val) =>
        val ? moment.utc(val).local().format("DD MMM YYYY hh:mm A") : "",
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">User Roles</div>
          <Select
            style={{ width: "100%", minHeight: "25px", fontSize: "12px" }}
            onClick={(e) => e.stopPropagation()}
            allowClear
            className="Select Project"
            dropdownClassName="select-dropdown-custom"
            showSearch
            size="small"
            placeholder=""
            onChange={(e) => {
              setSearch({
                ...search,
                initiatorRole: e,
              });
              debouncedCallApi();
            }}
            optionFilterProp="children"
          >
            {userTypeOptions?.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "initiatorRole",
      key: "initiatorRole",
      sorter: true,
      width: "10%",
      sortOrder: sortedInfo?.columnKey === "initiatorRole" && sortedInfo?.order,
      render: (role) => {
        if (!role) return "";
        if (typeof role === "string") {
          return capitalizeFirstLetterofEachWord(role);
        }
        if (Array.isArray(role)) {
          return role
            .map((el) => capitalizeFirstLetterofEachWord(el))
            .join(", ");
        }
        return "-";
      },
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Duration</div>
          {searchInput("callDuration")}
        </div>
      ),
      dataIndex: "callDuration",
      key: "callDuration",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "callDuration" && sortedInfo?.order,
      align: "center",
      width: "10%",
      render: (d) => (d ? `${Math.floor(d / 60)}m ${Math.floor(d % 60)}s` : ""),
    },
    {
      title: () => (
        <div align="top" style={{ width: "100%" }}>
          <div className="special-table-header-text">Call Status</div>
          {searchInput("type")}
        </div>
      ),
      dataIndex: "type",
      key: "type",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "type" && sortedInfo?.order,
      align: "center",
      width: "10%",
      render: (t) => t?.toUpperCase() || "",
    },
  ];

  const onTableRowExpand = (expanded, record) => {
    setExpandedRowKeys(expanded ? [record._id] : []);
  };

  return (
    <div className="page-top-div">
      <span className="text-span">Call History</span>
      <Row className="production-manager-parent-row">
        <Col span={24} className="heading-col">
          <Row align="middle" gutter={[30, 0]} justify="space-between">
            <Col>
              <Row align="middle" gutter={[40, 0]}>
                <Col>
                  <div>
                    <span className="heading-span">Call History Manager</span>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Table
            className="special-table"
            columns={columns}
            scroll={{ x: !loading }}
            // dataSource={dataSource}
            dataSource={paginatedData}
            onChange={handleChange}
            pagination={false}
            sortUpIcon={<UpOutlined />}
            sortDownIcon={<DownOutlined />}
            loading={loading}
            onRow={({ _id }) => {
              return (
                expandedRowKeys.includes(_id) && {
                  className:
                    "expand-parent expand-doc-parent fourth-level-expanded-row",
                }
              );
            }}
            rowKey={(record) => record._id}
            expandable={{
              expandRowByClick: true,
              showExpandColumn: true,
              expandedRowKeys,
              onExpand: onTableRowExpand,
              expandedRowRender: (record) =>
                record.receivers?.length ? (
                  <ReceiversTable receivers={record.receivers} />
                ) : (
                  <div style={{ paddingLeft: 24 }}>No Receivers</div>
                ),
            }}
          />
          {/* <CustomPagination
            page={page}
            setPage={setPage}
            resultPerPage={resultPerPage}
            setResultPerPage={setResultPerPage}
            debouncedCallApi={debouncedCallApi}
            totalRecord={totalRecords}
          /> */}
          <CustomPagination
            page={page}
            setPage={setPage}
            resultPerPage={resultPerPage}
            setResultPerPage={setResultPerPage}
            debouncedCallApi={debouncedCallApi}
            totalRecord={dataSource.length}
          />
        </Col>
      </Row>
    </div>
  );
};

export default CallingTable;
